import { cloneElement, useCallback, useEffect, useMemo, useRef } from 'react';
import { t } from '@lingui/macro';
import clsx from 'clsx';
import { LinearProgress, Typography, CircularProgress } from '@material-ui/core';

import useStyles, { useLoaderStyles } from './DataTableStyles';
import { Toolbar, View } from './internal';
import { DataTablePropTypes, DataTableDefaultProps } from './DataTableProps';
import { TableStateProvider, useTableRowBounds, useTableSearch, useTableSorters } from './TableState';
import { DATATABLE_CLASS, ROWCLASS, ROWHEIGHT } from './DataTableConstants';
import DataStoreProvider, { useDataStoreReset } from './DataStore';
import SelectionProvider, { SELECTIONMODE_MULTI, useSelection, useSelectionActions } from './SelectionContext';
import ColumnProvider from './ColumnContext';
import RenderContextProvider from './internal/RenderContext';
import useSize from '../../utilities/UseSize';
import { setRef } from '../../utilities/RefUtilities';

export default function DataTable(props) {
    const {
        container: initialContainer,
        className,
        padded,
        tableId,
        showToolbar,
        showSearch,
        showHeaders,
        autoFocusSearch,
        visibleRows,
        shrink,
        rowSize,
        emptyText,
        loadingText,
        showLoader,
        loader,
        sorters: sortersProp,
        search: searchProp,
        params,
        limit,
        columns: configColumns,
        selection,
        selectionOptions,
        showSelectionColumn,
        stickySearch,
        state,
        onStateChanged,
        deletedMapping,
        disableCSVExport,
        disableColumnRemover,
        onScroll,
        triggerRef,
        columnOverrides,
        enableStickyState,
        selectionColumnRenderCondition,
        summaryElement,
    } = props;

    const classes = useStyles();
    const rowSizeClass = ROWCLASS[rowSize];
    const clearSelectionCallbackRef = useRef();
    const viewportRef = useRef();
    const viewportSize = useSize(viewportRef);
    const container = initialContainer || <div />;
    const rowHeight = ROWHEIGHT[rowSize];

    const idProps = {};
    if (tableId) {
        idProps.id = `datatable-${tableId}`;
    }

    return (
        <TableStateProvider
            tableId={tableId}
            onStateChanged={onStateChanged}
            sorters={sortersProp}
            search={searchProp}
            state={state}
            visibleRows={visibleRows}
            columnOverrides={columnOverrides}
            enableStickyState={enableStickyState}
        >
            <DataStoreParamsAndFilterMerger stickySearch={stickySearch}>
                {({ rowBounds, sorters, filters }) => (
                    <DataStoreProvider
                        options={{
                            rowBounds,
                            loader,
                            sorters,
                            params,
                            limit,
                            deletedMapping,
                            filters,
                        }}
                        onLoaderChange={clearSelectionCallbackRef.current}
                        onParamsChange={clearSelectionCallbackRef.current}
                        onLimitChange={clearSelectionCallbackRef.current}
                        onFiltersChange={clearSelectionCallbackRef.current}
                    >
                        {(dataStore) => (
                            <SelectionProvider
                                selection={selection}
                                canSelectCondition={selectionColumnRenderCondition}
                                options={{ totalRows: dataStore.total, ...selectionOptions }}
                            >
                                <ColumnProvider
                                    config={configColumns}
                                    showSelectionColumn={showSelectionColumn}
                                    selectionColumnRenderCondition={selectionColumnRenderCondition}
                                    viewportSize={viewportSize}
                                >
                                    <ResetSelectionListener callbackRef={clearSelectionCallbackRef} />
                                    <TriggerRefSetter triggerRef={triggerRef} />

                                    {cloneElement(
                                        container,
                                        {
                                            className: clsx(
                                                classes.root,
                                                className,
                                                { padded, 'full-height': visibleRows === 0 },
                                                DATATABLE_CLASS
                                            ),
                                            ...idProps,
                                        },

                                        <>
                                            {summaryElement}
                                            <div
                                                className={clsx(classes.dataTableRoot, rowSizeClass, {
                                                    'full-height': visibleRows === 0,
                                                })}
                                            >
                                                {showToolbar && (
                                                    <Toolbar
                                                        showSearch={showSearch}
                                                        autoFocusSearch={autoFocusSearch}
                                                        stickySearch={stickySearch}
                                                        loader={loader}
                                                        sorters={sorters}
                                                        filters={filters}
                                                        params={params}
                                                        disableCSVExport={disableCSVExport}
                                                        disableColumnRemover={disableColumnRemover}
                                                    />
                                                )}

                                                <div className={classes.loadingContainer}>
                                                    <div className={classes.loader}>
                                                        {dataStore.isLoading && showLoader && (
                                                            <LinearProgress variant="query" />
                                                        )}
                                                    </div>
                                                </div>

                                                <RenderContextProvider
                                                    rowHeight={rowHeight}
                                                    viewportRef={viewportRef}
                                                    visibleRows={visibleRows}
                                                    shrink={shrink}
                                                >
                                                    <View
                                                        showHeaders={showHeaders}
                                                        onScroll={onScroll}
                                                        viewportRef={viewportRef}
                                                        statusComponent={getStatusComponent(
                                                            dataStore,
                                                            emptyText,
                                                            loadingText,
                                                            filters
                                                        )}
                                                    />
                                                </RenderContextProvider>
                                            </div>
                                        </>
                                    )}
                                </ColumnProvider>
                            </SelectionProvider>
                        )}
                    </DataStoreProvider>
                )}
            </DataStoreParamsAndFilterMerger>
        </TableStateProvider>
    );
}

// ---- Prop Metadata ----
DataTable.propTypes = DataTablePropTypes;
DataTable.defaultProps = DataTableDefaultProps;

function getStatusComponent(dataStore, emptyText, loadingText, filters) {
    if (dataStore.isLoading && !dataStore.hasLoaded) {
        return <LoadingTextComponent text={loadingText} />;
    }

    if (dataStore.hasLoaded && !dataStore.isLoading && dataStore.total === 0) {
        return filters.length > 0 ? (
            <StatusTextComponent text={t`No data available`} />
        ) : (
            <StatusTextComponent text={emptyText} />
        );
    }

    return null;
}

export function StatusTextComponent(props) {
    const { text, className } = props;

    return (
        <Typography variant="subtitle2" component="div" className={className}>
            {text}
        </Typography>
    );
}

export function LoadingTextComponent(props) {
    const { text } = props;
    const classes = useLoaderStyles();

    return (
        <div className={classes.root}>
            <CircularProgress size={16} />
            <StatusTextComponent className={classes.textContainer} text={text} />
        </div>
    );
}

function ResetSelectionListener(props) {
    const { callbackRef } = props;
    const { mode } = useSelection();
    const { clear } = useSelectionActions();

    // we only set the clear selection listener in multi-select mode. single select
    // does not clear selections.
    callbackRef.current = mode === SELECTIONMODE_MULTI ? clear : null;

    return null;
}

function DataStoreParamsAndFilterMerger(props) {
    const { children, stickySearch } = props;

    const search = useTableSearch();
    const sorters = useTableSorters();
    const rowBounds = useTableRowBounds();
    const filters = useFilters(search, stickySearch);

    return children({ rowBounds, sorters, filters });
}

function TriggerRefSetter(props) {
    const { triggerRef } = props;
    const reset = useDataStoreReset();
    const { clear } = useSelectionActions();

    // triggerRef is a "ref". But ESLint doesn't know that. Store
    // it in another ref to avoid the dependency override. There's got
    // to be a way to mark a variable as not needing to be a part
    // of the dependencies without having to do this...
    const ref = useRef();
    ref.current = triggerRef;

    const triggerFn = useCallback(
        (clearSelection = true, hardReset = false) => {
            if (clearSelection) {
                clear();
            }

            reset(hardReset);
        },
        [clear, reset]
    );

    useEffect(() => {
        setRef(ref.current, triggerFn);
    }, [triggerFn]);

    return null;
}

function useFilters(search, stickySearch) {
    return useMemo(() => {
        const filters = [];

        if (search) {
            filters.push({ value: search });
        }

        if (stickySearch) {
            filters.push({ value: stickySearch });
        }

        return filters;
    }, [search, stickySearch]);
}
